import { editorContentSignature } from '@/editor/core/save-state'

export default {
  data: () => ({
    editorSaveReady: false,
    editorSavedSignature: null,
    editorSaveTicket: null,
    editorSaveGeneration: 0,
    editorSaveError: '',
    editorVersionSwitching: false,
    editorVersionConfirming: false,
    editorLeaveRequest: 0,
    editorPageSwitchRequest: 0
  }),
  computed: {
    editorContentSignature () {
      if (!this.editorSaveReady || !this.projectRecord) return null
      try { return editorContentSignature(this.pageProject, this.activePageId, this.config, this.nav) } catch (error) { return null }
    },
    editorDirty () {
      return this.editorSaveReady && (this.editorContentSignature === null || this.editorContentSignature !== this.editorSavedSignature)
    },
    editorSaveInProgress () { return Boolean(this.editorSaveTicket) },
    editorLeaveProtected () { return this.editorDirty || this.editorSaveInProgress || this.editorVersionSwitching },
    editorSaveStatus () {
      if (!this.editorSaveReady) return 'loading'
      if (this.editorSaveInProgress) return 'saving'
      if (this.editorSaveError) return 'error'
      return this.editorDirty ? 'dirty' : 'saved'
    },
    editorSaveStatusText () {
      return { loading: '等待加载', saving: '保存中…', error: '保存失败', dirty: '未保存', saved: '已保存' }[this.editorSaveStatus]
    },
    editorSaveStatusDescription () {
      if (this.isComponentTemplateEditing && this.templateSaveBlocked) return this.templateSaveBlocked
      if (this.editorSaveError) return this.editorSaveError + '。当前编辑内容仍保留，可重试保存。'
      if (this.editorSaveInProgress) return '正在后台保存，可继续编辑和切换页面。'
      if (this.editorDirty) return this.isTemplateEditing ? '修改尚未保存，可点击保存模板或按 Ctrl+S；预览和站内离开时也会先保存。' : '修改将自动在后台保存，也可按 Ctrl+S 立即保存。'
      return this.editorSaveReady ? '当前内容与本次加载或最近成功保存的内容一致。' : '项目配置尚未加载成功。'
    }
  },
  watch: {
    editorLeaveProtected: 'syncEditorBeforeUnload',
    editorContentSignature: 'scheduleEditorAutoSave'
  },
  mounted () {
    this.syncEditorBeforeUnload()
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', this.handleEditorVisibilityChange)
    if (typeof window !== 'undefined') window.addEventListener('pagehide', this.handleEditorPageHide)
    this._editorLifecycleRegistered = true
  },
  beforeDestroy () {
    if (!this.isTemplateEditing) this.saveEditorOnLifecycle()
    this.editorLeaveRequest++
    this.resetEditorSaveTracking()
    this.removeEditorBeforeUnload()
    if (this._editorLifecycleRegistered) {
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', this.handleEditorVisibilityChange)
      if (typeof window !== 'undefined') window.removeEventListener('pagehide', this.handleEditorPageHide)
      this._editorLifecycleRegistered = false
    }
  },
  beforeRouteLeave (to, from, next) { this.confirmEditorLeave(next) },
  beforeRouteUpdate (to, from, next) {
    // 与 container 的重载监听保持一致；项目内普通切页不经过路由。
    if (to.name !== from.name || to.params.id !== from.params.id || to.query.page !== from.query.page || to.query.preview !== from.query.preview) this.confirmEditorLeave(next)
    else next()
  },
  methods: {
    resetEditorSaveTracking () {
      clearTimeout(this._editorAutoSaveTimer)
      clearTimeout(this._editorPageSaveTimer)
      if (this._editorDraftObserver) this._editorDraftObserver()
      this._editorDraftObserver = null
      this._editorLeaveSave = null
      this._editorLifecycleSave = null
      this.editorSaveGeneration++
      this.editorSaveReady = false
      this.editorSavedSignature = null
      this.editorSaveTicket = null
      this.editorSaveError = ''
      this.editorVersionConfirming = false
    },
    initializeEditorSaveTracking () {
      this.editorSavedSignature = editorContentSignature(this.pageProject, this.activePageId, this.config, this.nav)
      this.editorSaveReady = true
      this.editorSaveError = ''
    },
    beginEditorSave () {
      if (!this.editorSaveReady || this.editorSaveInProgress) throw new Error('配置尚未就绪或正在保存，请稍后重试')
      const signature = editorContentSignature(this.pageProject, this.activePageId, this.config, this.nav)
      const ticket = Object.freeze({ generation: this.editorSaveGeneration, projectId: this.id, signature })
      this.editorSaveTicket = ticket
      this.editorSaveError = ''
      return ticket
    },
    isCurrentEditorSave (ticket) {
      return !this._isDestroyed && Boolean(ticket) && this.editorSaveTicket === ticket &&
        ticket.generation === this.editorSaveGeneration && ticket.projectId === this.id
    },
    completeEditorSave (ticket, signature = ticket.signature) {
      if (!this.isCurrentEditorSave(ticket)) return false
      this.editorSavedSignature = signature
      this.editorSaveTicket = null
      this.editorSaveError = ''
      this.scheduleEditorAutoSave()
      return true
    },
    failEditorSave (error, ticket) {
      if (!this.editorSaveReady || (ticket ? !this.isCurrentEditorSave(ticket) : this.editorSaveInProgress)) return false
      this.editorSaveTicket = null
      this.editorSaveError = error && error.message || '大屏配置保存失败，请稍后重试'
      return true
    },
    syncEditorBeforeUnload () {
      if (typeof window === 'undefined') return
      if (this.editorLeaveProtected && !this._editorBeforeUnloadRegistered && !this._isDestroyed) {
        window.addEventListener('beforeunload', this.handleEditorBeforeUnload)
        this._editorBeforeUnloadRegistered = true
      } else if (!this.editorLeaveProtected) this.removeEditorBeforeUnload()
    },
    removeEditorBeforeUnload () {
      if (this._editorBeforeUnloadRegistered && typeof window !== 'undefined') window.removeEventListener('beforeunload', this.handleEditorBeforeUnload)
      this._editorBeforeUnloadRegistered = false
    },
    handleEditorBeforeUnload (event) {
      this.saveEditorOnLifecycle()
      if (!this.editorLeaveProtected) return
      event.preventDefault()
      event.returnValue = ''
      return ''
    },
    handleEditorVisibilityChange () {
      if (document.visibilityState === 'hidden') this.saveEditorOnLifecycle()
    },
    handleEditorPageHide () { this.saveEditorOnLifecycle() },
    scheduleEditorAutoSave () {
      clearTimeout(this._editorAutoSaveTimer)
      if (this.isTemplateEditing || !this.editorSaveReady || !this.editorDirty || this.editorVersionSwitching || this.editorVersionConfirming || this.editorSaveError || this._isDestroyed) return
      // 短暂合并连续输入；不对每个拖动帧创建历史记录。
      this._editorAutoSaveTimer = setTimeout(() => { if (!this.editorSaveError && !this.editorVersionConfirming) this.saveEditorOnLifecycle() }, 800)
    },
    saveEditorOnLifecycle (pageId) {
      clearTimeout(this._editorAutoSaveTimer)
      if (!this.editorSaveReady || this.editorVersionSwitching || this._isDestroyed ||
        (!this.editorDirty && !this.editorSaveInProgress)) return Promise.resolve(false)
      if (!this.isTemplateEditing) {
        const toolbar = this.$refs.top
        if (!toolbar || typeof toolbar.handleBuild !== 'function') return Promise.resolve(false)
        const pending = toolbar.handleBuild({ quiet: true, background: true, ...(typeof pageId === 'string' ? { pageId } : {}) }).then(Boolean).finally(() => {
          if (this._editorLifecycleSave === pending) this._editorLifecycleSave = null
        })
        this._editorLifecycleSave = pending
        return pending
      }
      // 连续的 hidden/beforeunload/pagehide 共用任务，不重复新增记录。
      if (this._editorLifecycleSave) return this._editorLifecycleSave
      const generation = this.editorSaveGeneration, toolbar = this.$refs.top
      if (!toolbar || typeof toolbar.handleBuild !== 'function') return Promise.resolve(false)
      const save = async () => {
        do {
          // 不等待 nextTick，离页事件中立即发送；已有手动保存可提升为后台提交。
          const saved = await toolbar.handleBuild({ quiet: true, background: true })
          if (!saved || this._isDestroyed || generation !== this.editorSaveGeneration || this.editorVersionSwitching) return false
        } while (this.editorDirty)
        return true
      }
      const pending = save().catch(() => false).finally(() => {
        if (this._editorLifecycleSave === pending) this._editorLifecycleSave = null
      })
      this._editorLifecycleSave = pending
      return pending
    },
    async confirmEditorLeave (next) {
      const request = ++this.editorLeaveRequest
      if (this.editorVersionSwitching) {
        this.$message.warning('正在切换草稿，请等待完成后再离开')
        next(false)
        return
      }
      if (!this.editorSaveReady) { next(); return }
      if (!this.isTemplateEditing) {
        this.syncActivePage()
        this.saveEditorOnLifecycle()
        next()
        return
      }
      // 保存按钮成功后直接返回，不为干净的页面/项目模板再追加一个版本。
      if (!this.editorDirty && !this.editorSaveInProgress) { next(); return }
      const generation = this.editorSaveGeneration
      // 共用顶部保存任务；快速导航只有最后一次请求可以离开。
      if (!this._editorLeaveSave) {
        this._editorLeaveSave = this.saveEditorBeforeAction(generation)
      }
      const saving = this._editorLeaveSave
      let saved
      try { saved = await saving } catch (error) { this.$message.warning('自动保存失败，当前修改仍保留，请重试保存') }
      if (this._editorLeaveSave === saving) this._editorLeaveSave = null
      if (this._isDestroyed || request !== this.editorLeaveRequest || generation !== this.editorSaveGeneration) { next(false); return }
      if (!saved || this.editorSaveInProgress || this.editorVersionSwitching) { next(false); return }
      if (this.editorDirty) { this.$message.warning('保存期间有新的修改，请再次离开以保存最新内容'); next(false); return }
      next()
    },
    async switchEditorPage (id) {
      const request = ++this.editorPageSwitchRequest
      if (!this.projectRecord || !this.editorSaveReady || this.editorVersionSwitching ||
        !this.pageProject || !this.pageProject.pages.some(page => page.id === id)) return false
      if (id === this.activePageId) return true
      if (!this.isTemplateEditing) {
        // 离开的页先同步进项目；切页只改内存视图，不等待网络或截图。
        const pageId = this.activePageId
        this.syncActivePage()
        this.switchPage(id)
        clearTimeout(this._editorPageSaveTimer)
        this._editorPageSaveTimer = setTimeout(() => this.saveEditorOnLifecycle(pageId), 0)
        return true
      }
      const generation = this.editorSaveGeneration, sourcePageId = this.activePageId
      try {
        const saved = await this.saveEditorBeforeAction(generation)
        if (!saved || this._isDestroyed || request !== this.editorPageSwitchRequest ||
          generation !== this.editorSaveGeneration || sourcePageId !== this.activePageId ||
          this.editorVersionSwitching || !this.pageProject.pages.some(page => page.id === id)) return false
        this.switchPage(id)
        return true
      } catch (error) {
        if (!this._isDestroyed) this.$message.warning(error.message || '自动保存失败，当前页面和修改仍保留，请重试')
        return false
      }
    },
    async saveEditorBeforeAction (generation = this.editorSaveGeneration) {
      const toolbar = this.$refs.top
      if (!toolbar || typeof toolbar.handleBuild !== 'function') {
        this.$message.warning('保存入口尚未就绪，请稍后重试')
        return false
      }
      do {
        await this.$nextTick()
        if (this._isDestroyed || generation !== this.editorSaveGeneration || this.editorVersionSwitching) return false
        const saved = await toolbar.handleBuild({ quiet: true })
        if (!saved || this._isDestroyed || generation !== this.editorSaveGeneration || this.editorVersionSwitching) return false
        // 预览、切页、离开共用保存；期间新增的编辑补存成功后再继续。
        if (this.editorSaveInProgress) return false
      } while (this.editorDirty)
      return true
    }
  }
}
